import { getDisplayableAircraftData } from "@nova/constants"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { Input } from "@nova/ds-component-input"
import { ButtonDestructive, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { FunctionComponent, useState } from "react"
import { useDeleteAircraftBatch } from "./AircraftQueries"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useAircraftStatuses } from "./AircraftStatusQueries"
import { AircraftStatusBadgeDisplay } from "./AircraftStatusBadge"

interface Aircraft {
    id: string
    nickname: string | null
    model: string | null
    manufacturer: string | null
    manual_status: string | null
    lastFlight: Date | null
}

interface DeleteMultipleAircraftModalProps {
    aircraft: Aircraft[]
    onSubmit: () => void
    onCancel: () => void
}

const DeleteMultipleAircraftModal: FunctionComponent<DeleteMultipleAircraftModalProps> = ({
    aircraft,
    onCancel,
    onSubmit,
}) => {
    const { deleteAircraftBatch, isDeleting } = useDeleteAircraftBatch()
    const statuses = useAircraftStatuses(aircraft)
    const [confirmationInput, setConfirmationInput] = useState("")

    const confirmationPhrase = `delete ${aircraft.length} drones`
    const canDelete = confirmationInput === confirmationPhrase && !isDeleting

    const handleDelete = async () => {
        await deleteAircraftBatch(aircraft.map((a) => a.id))
        onSubmit()
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-lg w-full"
            title={`Delete ${aircraft.length} Drones?`}
            closeModal={onCancel}
            bottomBar={
                <div className="flex justify-end gap-2 w-full">
                    <ButtonTertiary title="Cancel" onClick={onCancel} />
                    <ButtonDestructive
                        title={`Delete ${aircraft.length} Drones`}
                        LeftIcon={Icons.Trash}
                        onClick={handleDelete}
                        disabled={!canDelete}
                    />
                </div>
            }
        >
            <div className="flex flex-col gap-4 pt-2 items-center">
                <h4 className="text-gray-0">
                    This will disconnect every flight that references these drones.
                </h4>
                <h4 className="text-red-500">This cannot be undone.</h4>
                <ScrollableArea className="w-full max-h-60">
                    <div className="flex flex-col w-full border border-gray-700 rounded-lg p-4 gap-5">
                        {aircraft.map((a, i) => {
                            const { displayName, model, make } = getDisplayableAircraftData(a)
                            return (
                                <div key={a.id} className="flex items-center gap-3">
                                    <span className="text-gray-400 w-4 shrink-0">{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-white truncate">{displayName}</h4>
                                        <h5 className="text-gray-200 truncate">
                                            {[make, model].filter(Boolean).join(" ") || "—"}
                                        </h5>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                        <AircraftStatusBadgeDisplay
                                            status={statuses?.[a.id] ?? null}
                                        />
                                        <small className="text-gray-400">
                                            {a.lastFlight
                                                ? `Last flown ${a.lastFlight.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
                                                : "—"}
                                        </small>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </ScrollableArea>
                <Input
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.currentTarget.value)}
                />
                <h5 className="text-gray-100 text-center">
                    Type the name &ldquo;{confirmationPhrase}&rdquo; to delete these drones.
                </h5>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { DeleteMultipleAircraftModal }
